import React from "react";
import {
  YOUTUBE, FIGHT_PREVIZ_THUMB, ANIMATICS_THUMB, MOCAP_THUMB, MOCAP_OUTPUT_THUMB, PREVIS_LEAD, PREVIS_PROCESS,
  ANIMATICS_BODY, MOCAP_BODY, MOCAP_BODY_2,
} from "../../data/content";
import { Reveal, Kicker, RevealHeading, PlayableVideo } from "./primitives";

const ValueChip = ({ big, label }) => (
  <div className="border border-border p-5 md:p-8 bg-background">
    <div className="font-display text-4xl sm:text-5xl md:text-6xl leading-none text-[hsl(var(--accent))]">{big}</div>
    <div className="kicker mt-2.5 md:mt-3 max-w-[280px] text-[9px] md:text-xs">{label}</div>
  </div>
);

const ProcessCard = ({ n, title, body, i }) => (
  <Reveal delay={i * 0.08}>
    <div className="bg-[#0d0d0d] p-5 sm:p-7 md:p-8 min-h-[200px] sm:min-h-[280px] md:min-h-[320px] flex flex-col h-full">
      <span className="font-mono-c text-xs text-[hsl(var(--accent))]">{n}</span>
      <h3 className="font-display uppercase text-2xl sm:text-3xl md:text-4xl leading-none mt-auto pt-8 sm:pt-16 tracking-tight">
        {title}
      </h3>
      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-3 sm:mt-4">{body}</p>
    </div>
  </Reveal>
);

export const Previs = () => (
  <section id="previs" className="relative py-14 md:py-40 px-4 md:px-10 bg-[#0d0d0d] border-y border-border" data-testid="previs">
    <div className="mx-auto max-w-[1600px]">
      {/* Header — split */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 items-end">
        <div className="lg:col-span-7">
          <Kicker index="06">Fight Previsualisation</Kicker>
          <RevealHeading text="Test The Action" className="display-h2 mt-3 md:mt-5" />
          <RevealHeading text="Before The Set." className="display-h2 text-[hsl(var(--accent))]" />
        </div>
        <Reveal className="lg:col-span-5">
          <p className="text-sm md:text-lg leading-relaxed text-muted-foreground">{PREVIS_LEAD}</p>
        </Reveal>
      </div>

      {/* Process grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-10 md:mt-20 border border-border bg-border">
        {PREVIS_PROCESS.map((p, i) => (
          <ProcessCard key={p.n} {...p} i={i} />
        ))}
      </div>

      {/* Impact stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
        <ValueChip big="15–20%" label="Potential reduction in action-sequence production cost" />
        <ValueChip big="Up to 40%" label="Lower previz execution cost at the CF / Chaos Faktory facility" />
      </div>

      {/* Live-action previs video */}
      <Reveal className="mt-10 md:mt-20" y={60}>
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <span className="kicker text-[10px] md:text-xs">Fight PreViz — Live Action</span>
          <span className="kicker text-[10px] md:text-xs hidden sm:inline">In-house · Shot &amp; Cut</span>
        </div>
        <PlayableVideo youtubeId={YOUTUBE.fightPrevis} thumbnail={FIGHT_PREVIZ_THUMB} alt="Fight PreViz Live Action" testid="video-fight-previs" aspect="aspect-[16/10] md:aspect-[16/7]" />
      </Reveal>

      {/* ---- Nested: 3D Animatics ---- */}
      <div className="mt-16 md:mt-40 pt-10 md:pt-16 border-t border-border">
        <div className="max-w-3xl">
          <Kicker index="06.1">3D Animatics · StuntViz</Kicker>
          <RevealHeading text="Animatics For" className="font-display uppercase text-3xl sm:text-4xl md:text-6xl mt-3 md:mt-5 leading-[0.9]" />
          <RevealHeading text="Complex Action" className="font-display uppercase text-3xl sm:text-4xl md:text-6xl leading-[0.9] text-muted-foreground" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mt-8 md:mt-12 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-xs sm:text-sm md:text-base leading-relaxed text-muted-foreground">{ANIMATICS_BODY}</p>
            <ul className="mt-6 sm:mt-8 space-y-2.5 sm:space-y-3">
              {["Vehicle Chases & Crashes", "Building Collapses & Destruction", "Explosions, Fire & Pyrotechnic Action", "Helicopter, Aircraft & Aerial Sequences", "Creature & Animal Action", "Crowd Battles & Large-Scale Combat", "Water, Height & Extreme-Environment Sequences", "CGI-Driven & VFX-Heavy Set Pieces"].map((t) => (
                <li key={t} className="flex items-center gap-2.5 sm:gap-3 font-mono-c text-[10px] sm:text-xs tracking-[0.12em] sm:tracking-[0.15em] uppercase text-foreground/80">
                  <span className="h-1.5 w-1.5 bg-[hsl(var(--accent))]" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.1} y={60}>
            <PlayableVideo youtubeId={YOUTUBE.animatics} thumbnail={ANIMATICS_THUMB} alt="3D Animatics StuntViz" testid="video-animatics" aspect="aspect-video" />
          </Reveal>
        </div>
      </div>

      {/* ---- Nested: Motion Capture ---- */}
      <div className="mt-16 md:mt-40 pt-10 md:pt-16 border-t border-border">
        <div className="max-w-3xl">
          <Kicker index="06.2">Motion Capture</Kicker>
          <RevealHeading text="Physical Performance." className="font-display uppercase text-3xl sm:text-4xl md:text-6xl mt-3 md:mt-5 leading-[0.9]" />
          <RevealHeading text="Digital Characters." className="font-display uppercase text-3xl sm:text-4xl md:text-6xl leading-[0.9] text-[hsl(var(--accent))]" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 mt-6 md:mt-8">
          <Reveal>
            <p className="text-xs sm:text-sm md:text-base leading-relaxed text-muted-foreground">{MOCAP_BODY}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs sm:text-sm md:text-base leading-relaxed text-muted-foreground">{MOCAP_BODY_2}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8 md:mt-12">
          <Reveal y={60}>
            <span className="kicker block mb-3 md:mb-4 text-[10px] md:text-xs">Mocap — Capture Session</span>
            <PlayableVideo youtubeId={YOUTUBE.mocap} thumbnail={MOCAP_THUMB} alt="Motion Capture Session" testid="video-mocap" aspect="aspect-video" />
          </Reveal>
          <Reveal delay={0.1} y={60}>
            <span className="kicker block mb-3 md:mb-4 text-[10px] md:text-xs">Mocap — Final Output</span>
            <PlayableVideo youtubeId={YOUTUBE.mocapOutput} thumbnail={MOCAP_OUTPUT_THUMB} alt="Motion Capture Output" testid="video-mocap-output" aspect="aspect-video" />
          </Reveal>
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-3 mt-8 md:mt-10">
          {["Technicolor", "Zebu", "ABAI Motion Capture", "Annapurna Studios"].map((c) => (
            <span key={c} className="border border-border px-3 sm:px-4 py-1.5 sm:py-2 font-mono-c text-[10px] sm:text-[11px] tracking-[0.15em] uppercase text-muted-foreground">
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);
